export type AppTheme = "default" | "ipgr";

export type AppSettings = {
  postgrestUrl: string;
  theme: AppTheme;
};

const ENV_POSTGREST_URL = String(import.meta.env.VITE_POSTGREST_URL || "").trim();
export const DEFAULT_POSTGREST_URL = ENV_POSTGREST_URL || "http://192.168.1.69:3001";
export const DEFAULT_THEME: AppTheme = "default";

const STORAGE_KEY = "appSettings";

function normalizeUrl(url?: string) {
  const trimmed = (url || "").trim();
  if (!trimmed) return DEFAULT_POSTGREST_URL;
  if (trimmed.startsWith("/")) return trimmed.replace(/\/+$/, "") || "/";
  const withScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(trimmed)
    ? trimmed
    : `http://${trimmed}`;
  const normalized = withScheme.replace(/\/+$/, "");
  return normalized || DEFAULT_POSTGREST_URL;
}

function normalizeTheme(theme?: string): AppTheme {
  return theme === "ipgr" ? "ipgr" : DEFAULT_THEME;
}

function loadSettings(): AppSettings {
  if (typeof window === "undefined") {
    return { postgrestUrl: DEFAULT_POSTGREST_URL, theme: DEFAULT_THEME };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AppSettings>;
      return {
        postgrestUrl: normalizeUrl(parsed.postgrestUrl),
        theme: normalizeTheme(parsed.theme)
      };
    }
  } catch {
    // ignore parse/storage errors and fall back to defaults
  }
  return { postgrestUrl: DEFAULT_POSTGREST_URL, theme: DEFAULT_THEME };
}

let settings = loadSettings();
applyTheme(settings.theme);

function persist(next: AppSettings) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore write failures (e.g., storage disabled)
  }
}

export function getAppSettings(): AppSettings {
  return { ...settings };
}

export function getPostgrestUrl(): string {
  return settings.postgrestUrl || DEFAULT_POSTGREST_URL;
}

export function getTheme(): AppTheme {
  return settings.theme || DEFAULT_THEME;
}

export function applyTheme(theme: AppTheme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

export function setAppSettings(next: AppSettings) {
  settings = {
    postgrestUrl: normalizeUrl(next.postgrestUrl),
    theme: normalizeTheme(next.theme)
  };
  persist(settings);
  applyTheme(settings.theme);
}

export function setPostgrestUrl(url: string) {
  setAppSettings({ postgrestUrl: url, theme: settings.theme });
}

export function setTheme(theme: AppTheme) {
  setAppSettings({ postgrestUrl: settings.postgrestUrl, theme });
}
